import {
  Box,
  Divider,
  Flex,
  MobileDataTableCardSurface,
  Text,
  type MobileCardRendererProps,
  type Trade,
} from '@talos/kyoko';
import { useCompactMobileCardLayout } from '../useCompactMobileCardLayout';

export const MOBILE_TRADE_CARD_HEIGHT = 100;
export const COMPACT_MOBILE_TRADE_CARD_HEIGHT = 75;

export function useMobileTradeCardLayout() {
  return useCompactMobileCardLayout(MOBILE_TRADE_CARD_HEIGHT, COMPACT_MOBILE_TRADE_CARD_HEIGHT);
}

/**
 * Mobile card for a single Trade. Designed to be passed to `MobileDataTable`
 * via the `cardRenderer` prop.
 *
 * Layout:
 *  - Header row: symbol + market account, with trade status badge on right
 *  - Meta row: transact time and short trade ID (compact also inlines user and order ID)
 *  - Divider (expanded)
 *  - Order ID + user (expanded)
 *  - Side + quantity @ price
 */
export function MobileTradeCard({ renderCell, onPress, compact = false }: MobileCardRendererProps<Trade>) {
  return (
    <MobileDataTableCardSurface
      type="button"
      data-testid="mobile-trade-card"
      onClick={onPress}
      $compact={compact}
      $gap={4}
    >
      <Flex justifyContent="space-between" alignItems="center" gap="spacing4">
        <Flex alignItems="center" gap="spacing4" overflow="hidden">
          <Box
            flex="1 0 auto"
            overflow="hidden"
            textOverflow="clip"
            whiteSpace="nowrap"
            data-testid="mobile-trade-card-symbol"
          >
            {renderCell('Symbol')}
          </Box>
          <Box
            flex="1 1 auto"
            overflow="hidden"
            textOverflow="clip"
            whiteSpace="nowrap"
            data-testid="mobile-trade-card-market-account"
            textTransform="uppercase"
            color="colorTextSubtle"
          >
            {renderCell('MarketAccount')}
          </Box>
        </Flex>
        <Box data-testid="mobile-trade-card-status" flex="0 0 auto">
          {renderCell('TradeStatus')}
        </Box>
      </Flex>

      <Flex justifyContent="space-between" alignItems="center" fontSize="fontSizeTiny" gap="spacing4" overflow="hidden">
        <Flex alignItems="center" gap="spacing4" overflow="hidden" minWidth={0}>
          <Box data-testid="mobile-trade-card-transact-time">{renderCell('TransactTime')}</Box>
          {compact && (
            <>
              <Divider orientation="vertical" />
              <Box data-testid="mobile-trade-card-user">{renderCell('User')}</Box>
            </>
          )}
          {compact && (
            <>
              <Divider orientation="vertical" />
              <Text data-testid="mobile-trade-card-order-id-label" color="colorTextSubtle">
                Order
              </Text>
              <Box data-testid="mobile-trade-card-order-id">{renderCell('OrderID')}</Box>
              <Divider orientation="vertical" />
            </>
          )}
        </Flex>
        <Flex alignItems="center" gap="spacing4" flex="0 0 auto">
          <Text data-testid="mobile-trade-card-id-label" color="colorTextSubtle">
            ID
          </Text>
          <Box data-testid="mobile-trade-card-trade-id">{renderCell('TradeID')}</Box>
        </Flex>
      </Flex>

      {!compact && <Divider color="backgroundDivider" />}

      {!compact && (
        <Flex justifyContent="space-between" alignItems="center" fontSize="fontSizeSmall">
          <Flex alignItems="center" gap="spacing4" overflow="hidden">
            <Text data-testid="mobile-trade-card-order-id-label" color="colorTextSubtle">
              Order
            </Text>
            <Box data-testid="mobile-trade-card-order-id">{renderCell('OrderID')}</Box>
          </Flex>
          <Flex alignItems="center" gap="spacing4" overflow="hidden">
            <Text data-testid="mobile-trade-card-user-label" color="colorTextSubtle">
              User
            </Text>
            <Box data-testid="mobile-trade-card-user">{renderCell('User')}</Box>
          </Flex>
        </Flex>
      )}

      <Flex justifyContent="space-between" alignItems="center" fontSize="fontSizeSmall">
        <Box data-testid="mobile-trade-card-side">{renderCell('Side')}</Box>
        <Flex alignItems="center" gap="spacing4" overflow="hidden">
          <Box data-testid="mobile-trade-card-quantity">{renderCell('Quantity')}</Box>
          <Text color="colorTextSubtle" fontWeight={500}>
            @
          </Text>
          <Box data-testid="mobile-trade-card-price">{renderCell('Price')}</Box>
        </Flex>
      </Flex>
    </MobileDataTableCardSurface>
  );
}
